/**
 * ApiCollectionTree — version tree of API collections.
 *
 * Every version of a collection (same collection_name) hangs from the version
 * it derives from. parent_collection_id comes resolved by the backend (the
 * recorded fork origin, or semver for versions that predate lineage — see
 * ApiCollectionLineage in core-ms), so this only arranges rows.
 *
 * Used by the API Collections dialog and the collection pickers of the
 * create / edit biz event wizards.
 */
angular.module('bizmetryApp').factory('ApiCollectionTree', function () {

    function semver(version) {
        var m = /^v?(\d+)(?:\.(\d+))?(?:\.(\d+))?/i.exec(String(version || '').trim());
        return m ? [+m[1], +(m[2] || 0), +(m[3] || 0)] : null;
    }

    function compareVersions(a, b) {
        var va = semver(a.api_version), vb = semver(b.api_version);
        if (va && vb) {
            for (var i = 0; i < 3; i++) { if (va[i] !== vb[i]) { return va[i] - vb[i]; } }
            return 0;
        }
        return String(a.api_version || '').localeCompare(String(b.api_version || ''));
    }

    /**
     * Pre-order rows { col, depth, parent, children, childCount, descendantCount }.
     * Families keep the order of their first version in `list`; versions whose
     * parent isn't in `list` (filtered out, retired…) show as base versions.
     */
    function build(list) {
        var families = [], byName = {};
        (list || []).forEach(function (c) {
            if (!byName[c.collection_name]) { byName[c.collection_name] = []; families.push(c.collection_name); }
            byName[c.collection_name].push(c);
        });

        var rows = [];
        families.forEach(function (name) {
            var members = byName[name];
            var byId = {};
            members.forEach(function (m) { byId[m.collection_id] = m; });

            var children = {}, roots = [];
            members.forEach(function (m) {
                var parent = m.parent_collection_id ? byId[m.parent_collection_id] : null;
                if (parent && parent !== m) {
                    (children[parent.collection_id] = children[parent.collection_id] || []).push(m);
                } else {
                    roots.push(m);
                }
            });

            var seen = {};
            function walk(col, depth, parent) {
                if (seen[col.collection_id]) { return 0; }
                seen[col.collection_id] = true;
                var row = { col: col, depth: depth, parent: parent, children: [], childCount: 0, descendantCount: 0 };
                rows.push(row);
                if (parent) { parent.children.push(row); }
                (children[col.collection_id] || []).slice().sort(compareVersions).forEach(function (k) {
                    var added = walk(k, depth + 1, row);
                    if (added) { row.childCount++; row.descendantCount += added; }
                });
                return 1 + row.descendantCount;
            }
            roots.sort(compareVersions).forEach(function (r) { walk(r, 0, null); });
            // A lineage cycle would leave members unvisited — show them as roots.
            members.forEach(function (m) { if (!seen[m.collection_id]) { walk(m, 0, null); } });
        });
        return rows;
    }

    /** Rows whose ancestors are all expanded (expanded: collection_id → true). */
    function visibleRows(rows, expanded) {
        return rows.filter(function (row) {
            for (var p = row.parent; p; p = p.parent) {
                if (!expanded[p.col.collection_id]) { return false; }
            }
            return true;
        });
    }

    /**
     * Stateful view for pickers that re-render from a function each digest:
     * rows(list, opts) is memoized (same row objects until something changes),
     * so ng-repeat doesn't see new rows every digest.
     *   opts.expandAll — expand everything (e.g. while searching)
     *   opts.reveal    — collection_id → true that must be visible: their
     *                    branches get expanded once (the user can collapse again)
     */
    function createView() {
        var expanded = {}, revealed = {};
        var cache = { key: null, rows: [] };

        function trueKeys(map) {
            return Object.keys(map).filter(function (k) { return map[k]; }).sort().join(',');
        }

        function rows(list, opts) {
            opts = opts || {};
            var reveal = opts.reveal || {};
            var key = (list || []).map(function (c) { return c.collection_id + ':' + c.parent_collection_id; }).join(',') +
                '|' + (opts.expandAll ? 1 : 0) + '|' + trueKeys(expanded) + '|' + trueKeys(reveal);
            if (key === cache.key) { return cache.rows; }

            var all = build(list);
            all.forEach(function (row) {
                var id = row.col.collection_id;
                if (reveal[id] && !revealed[id]) {
                    revealed[id] = true;
                    for (var p = row.parent; p; p = p.parent) { expanded[p.col.collection_id] = true; }
                }
            });
            Object.keys(revealed).forEach(function (id) { if (!reveal[id]) { delete revealed[id]; } });

            cache = { key: key, rows: opts.expandAll ? all : visibleRows(all, expanded) };
            return cache.rows;
        }

        return {
            rows: rows,
            isExpanded: function (row, expandAll) { return !!(expandAll || expanded[row.col.collection_id]); },
            toggle: function (row, $event) {
                if ($event) { $event.stopPropagation(); }
                if (!row.childCount) { return; }
                expanded[row.col.collection_id] = !expanded[row.col.collection_id];
            }
        };
    }

    return {
        semver: semver,
        compareVersions: compareVersions,
        build: build,
        visibleRows: visibleRows,
        createView: createView
    };
});
